import React, { memo, useCallback, useState } from 'react';
import { InteractionManager, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import Animated, { SharedValue, interpolate, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import Svg, { Text as SvgText } from 'react-native-svg';

import { CommonActions, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { isIndividualEvent, isTeamEvent } from '@/api/events/types/types';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box, Column, Image, Row } from '@/components/lib/components';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { CollegeBasketballShirt } from '@/components/player-profile/tiles/college-sports/CollegeBasketballShirt';
import { CollegeFootballShirt } from '@/components/player-profile/tiles/college-sports/CollegeFootballShirt';
import { MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { useDebugSimulateLiveMarketsUpdates } from '@/feature/betslip-pickem/hooks/use-betslip-debug-hooks';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { useLocalFlagsStore } from '@/feature/devsettings/hooks/use-dev-settings';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/feature/fantasy-event-sheets/components/EventSheetTitle';
import { PlayerLastNameWithMask } from '@/feature/player-profile-sheets/components/PlayerLastNameWithMask';
import { PlayerProfileEventWidget } from '@/feature/player-profile-sheets/components/PlayerProfileEventWidget';
import { PlayerProfileSheetImageCanvas } from '@/feature/player-profile-sheets/components/PlayerProfileSheetImageCanvas';
import { PlayerProfileSheetPlaceholder } from '@/feature/player-profile-sheets/components/PlayerProfileSheetPlaceholder';
import { ProfileImageSilhouette } from '@/feature/player-profile-sheets/components/ProfileImageSilhouette';
import { ShieldSkiaCanvas } from '@/feature/player-profile-sheets/components/ShieldSkiaCanvas';
import {
    CONTAINER_SIZE,
    PADDING_HORIZONTAL,
    PADDING_RIGHT_PLAYER_IMAGE,
    getPlayerDisplayDetails,
} from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { CircleTopGlow } from '@/feature/promohub/components/CircleTopGlow';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { defaultZustandCompareFunction } from '@/utils/default-zustand-compare-function';
import { getPlayerJerseyNumber } from '@/utils/formatPlayerInfo';
import { isCollegeSport, isPGA } from '@/utils/league';
import { isPlayerImgPlaceholder } from '@/utils/player';

const GLOW_RADIUS = 393;
const BACKGROUND_IMAGE_SIZE = 176;
const BACKGROUND_TEAM_RIGHT_PADDING = 15;
const GLOW_HEIGHT = 168;
const TOP_DISTANCE = EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT;

export const PlayerProfileHeader = memo(
    ({
        scrollOffset,
        playerInfo,
        closeSinglePlayerProfile,
    }: {
        scrollOffset: SharedValue<number>;
        playerInfo: PlayerWithTeamAndEvent;
        closeSinglePlayerProfile?: () => void;
    }) => {
        const { width: screenWidth } = useWindowDimensions();
        const [playerHeaderLayout, setPlayerHeaderLayout] = useState<{ height: number; y: number }>({
            height: 0,
            y: 0,
        });
        const [hasErrorLoadingImage, setHasErrorLoadingImage] = useState(false);
        const { leagueIcon, leagueColor } = useLeagueConfigsStore(
            leagueConfigSelector(playerInfo.league),
            defaultZustandCompareFunction
        );
        const isPlaceholderProfileImage = isPlayerImgPlaceholder(playerInfo?.icon) || hasErrorLoadingImage;
        const paddingTopForPlayerProfileImage = isPGA(playerInfo.league) || isPlaceholderProfileImage ? 's4' : 's0';
        const playerNumber = getPlayerJerseyNumber(playerInfo.league, playerInfo?.jerseyNumber);
        const hasPlayerNumber = typeof playerNumber === 'number';
        const isCBB = playerInfo.league === League.Cbb;
        const glowHeight = useSharedValue(GLOW_HEIGHT);
        const fallbackColor = designSystem.colors.gray8;
        const topGlowColor = isTeamEvent(playerInfo.event)
            ? playerInfo?.team?.color || fallbackColor
            : leagueColor || fallbackColor;
        const paddingRightForPlayerProfileImage = isCollegeSport(playerInfo.league)
            ? isCBB
                ? 56
                : 42
            : PADDING_RIGHT_PLAYER_IMAGE;
        const paddingRight = isWeb
            ? paddingRightForPlayerProfileImage + PADDING_HORIZONTAL
            : paddingRightForPlayerProfileImage + PADDING_HORIZONTAL;
        const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
        const event = playerInfo?.event;

        const onImageError = useCallback(() => {
            setHasErrorLoadingImage(true);
        }, []);

        const onPressEventWidget = useCallback(() => {
            if (closeSinglePlayerProfile) {
                closeSinglePlayerProfile();
                return;
            }
            navigation.navigate('FantasyEventsSheets', {
                league: event?.league,
                eventId: event?.id || '',
                contextKey: new Date().getTime().toString(),
            });
            InteractionManager.runAfterInteractions(() => {
                navigation.dispatch(state => {
                    // resetting because we want to remove the PlayerProfileSheets from the stack when we navigate to the event sheet without closing the player profile sheet effect
                    return CommonActions.reset({
                        ...state,
                        routes: [...state.routes.filter(r => r.name !== 'PlayerProfileSheets')],
                        index: state.routes.length - 2,
                    });
                });
            });
        }, [closeSinglePlayerProfile, navigation, event?.league, event?.id]);

        const headerOpacityMaskStyle = useAnimatedStyle(() => {
            //The hight of the modal title, showing the player name and title
            const modalTitleHeight = EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT;
            //The mask should be drawn on top of the player header
            const maskBottomPositionY = playerHeaderLayout.height + playerHeaderLayout.y;
            //The mask will shrink as user scrolls up, but not more than the modal title height
            const maskHeight = Math.max(maskBottomPositionY - scrollOffset.value, modalTitleHeight);
            return {
                position: 'absolute',
                right: 0,
                left: 0,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                backgroundColor: designSystem.colors.gray8,
                opacity: interpolate(scrollOffset.value, [0, playerHeaderLayout.height], [0, 1], 'clamp'),
                height: maskHeight,
                zIndex: 2,
                transform: [{ translateY: scrollOffset.value }],
            };
        });

        return (
            <Box style={{ paddingTop: TOP_DISTANCE }}>
                <CircleTopGlow
                    color={topGlowColor}
                    cx={screenWidth / 2}
                    cy={-GLOW_RADIUS}
                    opacity={0.3}
                    radius={GLOW_RADIUS}
                    blur={115}
                    headerHeight={glowHeight}
                    style={styles.topGlow}
                />
                <LinearGradient
                    start={{ x: 0, y: 0 }}
                    end={{ x: 0, y: 1 }}
                    colors={[withOpacity(designSystem.colors.gray8, 0), withOpacity(designSystem.colors.gray8, 1)]}
                    positions={[0, 0.6944]}
                    style={styles.linearGradient}
                />
                <Row
                    onLayout={({ nativeEvent }) => {
                        setPlayerHeaderLayout(nativeEvent.layout);
                    }}
                    paddingHorizontal={'s16'}
                    justifyContent={'space-between'}
                    zIndex={2}
                >
                    <PlayerDetails playerInfo={playerInfo} />
                </Row>
                <Box
                    paddingTop={paddingTopForPlayerProfileImage}
                    justifyContent={'flex-start'}
                    style={[
                        {
                            right: paddingRight,
                        },
                        styles.profilePicture,
                        common.zIndex2,
                    ]}
                >
                    {hasPlayerNumber ? (
                        <PlayerNumberProfileImage playerInfo={playerInfo} />
                    ) : isPlaceholderProfileImage ? (
                        <PlayerProfileSheetPlaceholder
                            width={CONTAINER_SIZE.width}
                            height={CONTAINER_SIZE.height}
                            background={designSystem.colors.gray8}
                            opacity={0.4}
                        />
                    ) : (
                        <PlayerProfileSheetImageCanvas
                            key={playerInfo.id}
                            source={playerInfo.icon}
                            onError={onImageError}
                        />
                    )}
                </Box>
                {!hasPlayerNumber && !isPlaceholderProfileImage ? (
                    <Box
                        paddingTop={paddingTopForPlayerProfileImage}
                        justifyContent={'flex-start'}
                        style={[
                            {
                                right: paddingRight,
                            },
                            styles.profilePicture,
                            common.zIndexNegative1,
                        ]}
                    >
                        <ProfileImageSilhouette source={playerInfo.icon} onError={onImageError} />
                    </Box>
                ) : null}
                <PlayerProfileTeamBackgroundImage playerInfo={playerInfo} leagueIcon={leagueIcon} />
                {/** Drawing a mask that will fade in as the user scrolls the player header outside of the view port */}
                <Animated.View style={headerOpacityMaskStyle} />
                <Box px={'s16'} zIndex={1}>
                    <TouchableOpacity onPress={onPressEventWidget}>
                        <PlayerProfileEventWidget event={playerInfo?.event} player={playerInfo} />
                    </TouchableOpacity>
                    <DebugButtons playerId={playerInfo.id} />
                </Box>
            </Box>
        );
    }
);

const PlayerDetails = ({ playerInfo }: { playerInfo: PlayerWithTeamAndEvent }) => {
    const detailsText = getPlayerDisplayDetails(playerInfo);
    return (
        <Box marginTop={'s20'} marginBottom={'s16'} flex={1}>
            <Text
                variant={'titleMedium'}
                lineHeight={16}
                numberOfLines={1}
                ellipsizeMode={'tail'}
                textTransform={'uppercase'}
                fontFamily={'Gamay-CondSemiBold'}
            >
                {playerInfo.firstName}
            </Text>
            {isWeb ? (
                <Text
                    variant={'displayMedium'}
                    lineHeight={36}
                    fontSize={30}
                    numberOfLines={1}
                    ellipsizeMode={'tail'}
                    textTransform={'uppercase'}
                    fontFamily={'gamay-extrabold'}
                >
                    {playerInfo.lastName}
                </Text>
            ) : (
                <PlayerLastNameWithMask lastName={playerInfo.lastName} />
            )}
            <Row paddingTop="s4">
                <Text variant="bodyMedium" color="gray3">
                    {detailsText}
                </Text>
            </Row>
        </Box>
    );
};

const PlayerProfileTeamBackgroundImage = ({
    playerInfo,
    leagueIcon,
}: {
    playerInfo: PlayerWithTeamAndEvent;
    leagueIcon?: string;
}) => {
    return (
        <Box
            paddingBottom={'s20'}
            position={'absolute'}
            right={BACKGROUND_TEAM_RIGHT_PADDING}
            top={24}
            zIndex={-2}
            width={BACKGROUND_IMAGE_SIZE}
            height={BACKGROUND_IMAGE_SIZE}
        >
            {isTeamEvent(playerInfo.event) ? (
                isCollegeSport(playerInfo.league) ? (
                    <ShieldSkiaCanvas
                        primaryColor={playerInfo?.team?.color}
                        secondaryColor={playerInfo?.team?.secondaryColor}
                        width={BACKGROUND_IMAGE_SIZE}
                        height={BACKGROUND_IMAGE_SIZE}
                    />
                ) : (
                    <Image
                        source={{ uri: playerInfo.team?.largeIcon }}
                        style={styles.backgroundImage}
                        resizeMode={'contain'}
                    />
                )
            ) : isIndividualEvent(playerInfo.event) ? (
                <Image source={{ uri: leagueIcon }} style={styles.backgroundImage} resizeMode={'contain'} />
            ) : null}
        </Box>
    );
};

const PlayerNumberProfileImage = ({ playerInfo }: { playerInfo: PlayerWithTeamAndEvent }) => {
    const isCBB = playerInfo.league === League.Cbb;
    const teamColor = playerInfo?.team?.color;
    const teamSecondaryColor = playerInfo?.team?.secondaryColor;
    const playerNumber = getPlayerJerseyNumber(playerInfo.league, playerInfo?.jerseyNumber);
    const width = isCBB ? 57 : 88;
    const height = isCBB ? 99 : 87;

    return (
        <>
            <Box style={[styles.collegeShirtContainer, { width, height }]}>
                {isCBB ? (
                    <CollegeBasketballShirt
                        playerId={playerInfo.id}
                        teamColor={teamColor}
                        secondaryColor={teamSecondaryColor}
                        width={width}
                        height={height}
                        blur={10}
                        maskWidth={130}
                        maskHeight={130}
                        maskX={-20}
                        maskY={-10}
                    />
                ) : (
                    <CollegeFootballShirt
                        playerId={playerInfo.id}
                        teamColor={teamColor}
                        secondaryColor={teamSecondaryColor}
                        width={width}
                        height={height}
                        blur={11}
                        maskR={80}
                    />
                )}
            </Box>
            <Svg style={styles.collegeSvgNumber}>
                <SvgText
                    fill={designSystem.colors.white}
                    stroke={designSystem.colors.gray8}
                    strokeWidth="0.72"
                    y={height / 2 + 5}
                    x={width / 2}
                    fontSize="28.8"
                    fontWeight="600"
                    textAnchor="middle"
                    fontFamily="Oswald-SemiBold"
                    letterSpacing={-1.2}
                >
                    {playerNumber}
                </SvgText>
            </Svg>
        </>
    );
};

const DebugButtons = ({ playerId }: { playerId: string }) => {
    const { localFlags } = useLocalFlagsStore();
    const [debugSimulateLiveMarkets, setDebugSimulateLiveMarkets] = useState(false);

    useDebugSimulateLiveMarketsUpdates(debugSimulateLiveMarkets, playerId);

    return localFlags?.playerProfileDebugButtons?.enabled ? (
        <Column gap={'s2'}>
            <Text>{playerId}</Text>
            <Button
                label={'Add current values'}
                onPress={() => {
                    usePlayerPropsStore.getState().actions._debugEnableCurrentValues(playerId);
                }}
            />
            <Button
                label={'Suspend all markets'}
                onPress={() => {
                    usePlayerPropsStore.getState().actions._debugSuspendPlayerMarkets(playerId, 'all');
                }}
            />
            <Button
                label={'Simulate websockets market'}
                onPress={() => {
                    setDebugSimulateLiveMarkets(true);
                }}
            />
        </Column>
    ) : null;
};

const styles = StyleSheet.create({
    linearGradient: {
        position: 'absolute',
        width: '100%',
        height: 162,
        bottom: 0,
        zIndex: 1,
    },
    backgroundImage: {
        width: BACKGROUND_IMAGE_SIZE,
        height: BACKGROUND_IMAGE_SIZE,
        opacity: 0.08,
    },
    collegeShirtContainer: {
        alignItems: 'center',
        borderRadius: 18,
        overflow: 'hidden',
        marginTop: 8,
    },
    collegeSvgNumber: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 16,
    },
    topGlow: {
        top: 0,
        zIndex: 0,
    },
    profilePicture: {
        position: 'absolute',
        top: TOP_DISTANCE,
    },
});
